import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import type { CampaignResultsPage } from '../outreachTypes';
import { ratePercent } from './resultsCopy';
import { resultsMessages as messages } from './resultsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the results; each part uses its own slice of it */
import styles from './Results.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FunnelProps {
  funnel: CampaignResultsPage['funnel'];
  dayNumber: number;
  songTitle: string;
}

/**
 * The funnel of a campaign (design outreach-v2 n1): pitched, opened the song, listened 30 s+,
 * replied and added, each with its share of the pitches.
 */
const FunnelComponent: React.FC<FunnelProps> = ({ funnel, dayNumber, songTitle }) => {
  const intl = useIntl();
  const cells = useMemo(() => {
    const share = (part: number) => ratePercent(part, funnel.pitched);
    return [
      {
        key: 'pitched',
        label: intl.formatMessage(messages.pitched),
        value: funnel.pitched,
        width: funnel.pitched > 0 ? 100 : 0,
        rate: intl.formatMessage(messages.inDays, { count: Math.max(dayNumber, 0) }),
      },
      {
        key: 'opened',
        label: intl.formatMessage(messages.opened),
        value: funnel.opened,
        width: share(funnel.opened),
        rate: intl.formatMessage(messages.ofPitched, { percent: share(funnel.opened) }),
      },
      {
        key: 'listened',
        label: intl.formatMessage(messages.listened),
        value: funnel.listened,
        width: share(funnel.listened),
        rate: intl.formatMessage(messages.percent, { percent: share(funnel.listened) }),
      },
      {
        key: 'replied',
        label: intl.formatMessage(messages.replied),
        value: funnel.replied,
        width: share(funnel.replied),
        rate: intl.formatMessage(messages.repliedInterested, {
          percent: share(funnel.replied),
          count: funnel.interested,
        }),
      },
      {
        key: 'added',
        label: intl.formatMessage(messages.added, { song: songTitle }),
        value: funnel.added,
        width: share(funnel.added),
        rate: intl.formatMessage(messages.addedFollowers, {
          percent: share(funnel.added),
          followers: intl.formatNumber(funnel.addedFollowers),
        }),
      },
    ].map((cell) => (
      <div key={cell.key} className={styles.cell} data-testid={`funnel-${cell.key}`}>
        <span className={styles.cellKey}>{cell.label}</span>
        <span className={styles.cellValue}>{intl.formatNumber(cell.value)}</span>
        <div className={styles.bar} aria-hidden>
          <i style={{ width: `${Math.min(100, cell.width)}%` }} />
        </div>
        <span className={styles.cellRate}>{cell.rate}</span>
      </div>
    ));
  }, [dayNumber, funnel, intl, songTitle]);
  return <div className={styles.funnel}>{cells}</div>;
};

export const Funnel = TypedMemo(FunnelComponent);
